﻿import { withRouter } from '../router-compat';
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';

import Constant from 'utils/constants';
import {
  requireMaintenancePlan as _requireMaintenancePlan,
  requireMaintenancePayment,
} from 'helpers/user';
import { isMaintenanceDue } from 'helpers/program';
import { userAware } from './UserProvider';
import * as modalActions from 'components/Modal/Modal.ducks';

const CAN_VIEW_URL_MAP = {
  [Constant.maintenancePlan.constraints.canViewMessages]: [
    '/messages/:threadId',
  ],
  [Constant.maintenancePlan.constraints.canViewNotifications]: [],
  [Constant.maintenancePlan.constraints.canViewAcneProgramPDFs]: [],
};
const MAINTENANCE_DUE_MODAL_ID = 'Conversation/MAINTENANCE_DUE_MODAL_ID';

const requireMaintenancePlan = (WrappedComponent) => {
  const CheckMaintenancePlan = (props) => {
    const { user, history, location, match, setOpenModalId, ...restProps } = props;

    const checkCanViewUrl = useCallback(() => {
      if (!user.program.maintenance_plan_balance) {
        return true;
      }
      const canViewPermissions = Object.entries(
        user.program.maintenance_plan_balance.balance,
      ).filter(([key, _]) => key.startsWith('can_view'));
      
      const currentPath = location.pathname;
      return canViewPermissions.every(
        ([permission, value]) => {
          const requiresPermission = CAN_VIEW_URL_MAP[permission].some(pattern => matchPath({ path: pattern, end: false }, currentPath));
          return !requiresPermission || (requiresPermission && value);
        }
      );
    }, [user, location.pathname]);

    useEffect(() => {
      const currentPath = location.pathname;
      const isAlreadyOnMaintenancePlans = currentPath === '/maintenance-plans';
      if (!isAlreadyOnMaintenancePlans) {
        if (_requireMaintenancePlan(user) || !checkCanViewUrl()) {
          history.replace('/maintenance-plans');
        }
        else if (isMaintenanceDue(user.program)) {
          history.replace('/maintenance-plans');
        }
      }
      if (requireMaintenancePayment(user)) {
        setOpenModalId(MAINTENANCE_DUE_MODAL_ID);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, setOpenModalId, checkCanViewUrl, location.pathname]);

    // history, location and match are destructured above because this component's own
    // body needs them - which REMOVES them from restProps, so they must be passed back
    // explicitly or the wrapped component never sees them. At v4.9.2 only `user` was
    // destructured and the three flowed through the spread untouched; dropping them
    // crashed /treatment-plan/:id in production with "undefined is not an object
    // (evaluating 'this.props.match.params')". Breakage class 1(a) in CLAUDE.md, the same
    // defect as the icon defaultProps regression: destructuring a prop you still intend
    // to forward silently removes it from the rest object.
    return (
      <WrappedComponent
        {...restProps}
        user={user}
        history={history}
        location={location}
        match={match}
      />
    );
  };

  CheckMaintenancePlan.propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    setOpenModalId: PropTypes.func.isRequired,
  };

  return withRouter(
    connect(null, { setOpenModalId: modalActions.setOpenModalId })(
      userAware(CheckMaintenancePlan),
    ),
  );
};

export default requireMaintenancePlan;
